/**
 * ISMS Task Guide Page
 *
 * Rendered at /isms-journey/:taskId
 *
 * Shows the "how to do this" guidance for a single ISMS Journey task as a
 * numbered checkbox exercise. Tip progress is persisted per-task in
 * localStorage. When all tips are ticked the task is auto-marked complete
 * on the main journey; the user can also toggle completion manually.
 */

import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  ALL_TASKS,
  PHASES,
  loadManualChecks,
  loadTipChecks,
  saveManualChecks,
  saveTipChecks,
} from '../data/ismsJourneyData';

export const IsmsTaskGuidePage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();

  // Resolve task + owning phase
  const task = ALL_TASKS.find((t) => t.id === taskId);
  const phase = task ? PHASES.find((p) => p.tasks.some((t) => t.id === taskId)) : undefined;

  // ── Tip checkbox state ──────────────────────────────────────────────────────
  const [allTipData, setAllTipData] = useState<Record<string, number[]>>(loadTipChecks);

  const checkedTips = new Set<number>(task ? (allTipData[task.id] ?? []) : []);

  const toggleTip = (idx: number) => {
    if (!task) return;
    setAllTipData((prev) => {
      const current = new Set<number>(prev[task.id] ?? []);
      if (current.has(idx)) current.delete(idx);
      else current.add(idx);
      const next = { ...prev, [task.id]: [...current] };
      saveTipChecks(next);
      return next;
    });
  };

  // ── Task-level complete state ───────────────────────────────────────────────
  const [manualChecks, setManualChecks] = useState<Set<string>>(loadManualChecks);
  const isComplete = task ? manualChecks.has(task.id) : false;

  const setTaskComplete = (done: boolean) => {
    if (!task) return;
    setManualChecks((prev) => {
      const next = new Set(prev);
      if (done) next.add(task.id);
      else next.delete(task.id);
      saveManualChecks(next);
      return next;
    });
  };

  // Auto-mark complete when all tips are ticked
  useEffect(() => {
    if (!task) return;
    const total = task.guidance.tips.length;
    const checked = (allTipData[task.id] ?? []).length;
    if (total > 0 && checked === total && !manualChecks.has(task.id)) {
      setTaskComplete(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTipData]);

  // ── Not found guard ─────────────────────────────────────────────────────────
  if (!task || !phase) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-banner np-tone-danger mb-3">
          <i className="ri-error-warning-line"></i>Task not found.
        </div>
        <NavLink to="/isms-journey" className="np-btn np-btn-ghost">
          <i className="ri-arrow-left-line"></i>Back to Journey
        </NavLink>
      </div>
    );
  }

  const totalTips = task.guidance.tips.length;
  const checkedCount = checkedTips.size;
  const allDone = checkedCount === totalTips;
  const progressPct = totalTips > 0 ? Math.round((checkedCount / totalTips) * 100) : 0;

  // Map the phase's Bootstrap colour name onto a nimbus-premium tone
  const tone = phase.colour === 'secondary' ? 'neutral' : phase.colour;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page" style={{ maxWidth: 780 }}>

      {/* ── Back link ── */}
      <nav className="np-crumb" style={{ marginBottom: '1rem' }}>
        <NavLink to="/isms-journey">
          <i className="ri-arrow-left-line"></i> Back to ISMS Journey
        </NavLink>
      </nav>

      {/* ── Task header card ── */}
      <div className={`np-card np-card-accent-${tone}`}>
        <div className="np-card-head">
          <div className="d-flex align-items-center gap-2">
            <span className={`np-chip np-tone-${tone}`}>Phase {phase.number}</span>
            <span className="np-sub">{phase.name}</span>
          </div>
        </div>
        <div className="np-card-body">
          <div className="d-flex align-items-start gap-3 flex-wrap">
            {/* Icon */}
            <span className={`np-icon-badge-lg np-tone-${tone} flex-shrink-0`}>
              <i className={task.icon}></i>
            </span>
            {/* Title + description */}
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <h5 className="mb-0 fw-bold">{task.title}</h5>
                {isComplete && (
                  <span className={`np-chip np-tone-${tone}`}>
                    <i className="ri-check-line"></i>Complete
                  </span>
                )}
              </div>
              <p className="np-sub mb-2" style={{ fontSize: '0.875rem' }}>{task.description}</p>
              <div className="d-flex align-items-center gap-1 flex-wrap">
                {task.clauses.map((c) => (
                  <span key={c} className="np-chip np-tone-neutral">
                    ISO {c}
                  </span>
                ))}
              </div>
            </div>
            {/* Action button */}
            <div className="flex-shrink-0">
              <NavLink to={task.path} className="np-btn np-btn-ghost np-btn-sm">
                {task.actionLabel} <i className="ri-arrow-right-line"></i>
              </NavLink>
            </div>
          </div>
        </div>
      </div>

      {/* ── Intro ── */}
      <div className="np-card">
        <div className="np-card-head">
          <h2><i className="ri-information-line text-primary me-2"></i>What this is about</h2>
        </div>
        <div className="np-card-body">
          <p className="mb-0 text-body" style={{ lineHeight: 1.75, fontSize: '0.9rem' }}>
            {task.guidance.intro}
          </p>
        </div>
      </div>

      {/* ── Steps checklist ── */}
      <div className="np-card">
        {/* Card header with step counter */}
        <div className="np-card-head">
          <h2><i className="ri-list-check-2 me-2"></i>How to do this</h2>
          <span className={`np-chip np-tone-${allDone ? 'success' : 'neutral'} np-tabular`}>
            {checkedCount} of {totalTips} steps
          </span>
        </div>

        {/* Thin progress bar below header */}
        <div className="np-progress" style={{ height: 4, borderRadius: 0 }}>
          <div
            className={`np-fill np-tone-${allDone ? 'success' : tone}`}
            style={{ width: `${progressPct}%`, transition: 'width 0.3s ease' }}
          />
        </div>

        {/* Tip rows */}
        <div className="np-card-body p-0">
          {task.guidance.tips.map((tip, idx) => {
            const checked = checkedTips.has(idx);
            return (
              <div
                key={idx}
                className={`d-flex align-items-start gap-3 px-4 py-3${idx < totalTips - 1 ? ' border-bottom' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleTip(idx)}
              >
                {/* Numbered circle / tick */}
                <div
                  className={`np-step-badge${checked ? ' np-tone-success' : tone !== 'primary' ? ` np-tone-${tone}` : ''}`}
                  style={{ width: 28, height: 28, fontSize: '0.75rem', marginTop: 2 }}
                >
                  {checked ? (
                    <i className="ri-check-line" style={{ fontSize: '0.85rem' }}></i>
                  ) : (
                    idx + 1
                  )}
                </div>

                {/* Tip text */}
                <p
                  className="mb-0 flex-grow-1"
                  style={{
                    fontSize: '0.875rem',
                    lineHeight: 1.7,
                    color: checked ? 'var(--bs-secondary-color)' : 'var(--bs-body-color)',
                    textDecoration: checked ? 'line-through' : 'none',
                    textDecorationColor: 'var(--np-border-strong)',
                  }}
                >
                  {tip}
                </p>

                {/* Checkbox (stop propagation so the row click handler doesn't double-fire) */}
                <div className="flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={checked}
                    onChange={() => toggleTip(idx)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: '1.1rem', height: '1.1rem', cursor: 'pointer' }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* All done banner */}
        {allDone && (
          <div className="np-banner np-tone-success" style={{ borderRadius: 0 }}>
            <i className="ri-sparkling-2-line"></i>
            <span className="fw-semibold">All steps complete — great work!</span>
          </div>
        )}
      </div>

      {/* ── "Done when" callout ── */}
      <div className="np-banner np-tone-success mb-4">
        <i className="ri-checkbox-circle-line" style={{ fontSize: '1.15rem' }}></i>
        <div>
          <span className="fw-semibold d-block mb-1" style={{ fontSize: '0.85rem' }}>
            You're done when:
          </span>
          <span style={{ fontSize: '0.875rem', lineHeight: 1.65 }}>
            {task.guidance.doneWhen}
          </span>
        </div>
      </div>

      {/* ── Mark complete button ── */}
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <button
          className={`np-btn px-4 ${isComplete ? 'np-btn-ghost' : `np-btn-${tone}`}`}
          onClick={() => setTaskComplete(!isComplete)}
        >
          {isComplete ? (
            <>
              <i className="ri-close-circle-line"></i>Mark as incomplete
            </>
          ) : (
            <>
              <i className="ri-checkbox-circle-line"></i>Mark task as complete
            </>
          )}
        </button>

        {isComplete && (
          <span className={`text-${phase.colour} small`}>
            <i className="ri-check-double-line me-1"></i>This task is marked complete on your journey.
          </span>
        )}
      </div>

    </div>
  );
};
